import axios from "axios";
import { useAuth } from "@clerk/clerk-react";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api";

export interface Price {
  id: string;
  interval: "month" | "year";
  amount: number;
  currency: string;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  prices: Price[];
}

export interface ProductsResponse {
  products: Product[];
}

/**
 * Exchange a Clerk token for a JWT token
 */
export const getJwtToken = async (clerkToken: string): Promise<string> => {
  try {
    const response = await axios.post(`${API_URL}/auth/session`, {
      token: clerkToken,
    });
    return response.data.access_token;
  } catch (error) {
    console.error("Error exchanging tokens:", error);
    throw error;
  }
};

/**
 * Fetch available subscription products and prices
 */
export const getProducts = async (): Promise<ProductsResponse> => {
  const response = await axios.get(`${API_URL}/subscriptions/products`);
  return response.data;
};

/**
 * Create a checkout session for subscription
 */
export const createCheckoutSession = async (
  priceId: string,
  successUrl: string = `${window.location.origin}/dashboard?checkout=success`,
  cancelUrl: string = `${window.location.origin}/pricing?checkout=canceled`,
  clerkToken?: string
): Promise<{ url: string }> => {
  try {
    // First exchange the Clerk token for a JWT token
    let jwtToken;

    if (clerkToken) {
      jwtToken = await getJwtToken(clerkToken);
    } else {
      console.warn("No authentication token available");
      throw new Error("Authentication required");
    }

    const response = await axios.post(
      `${API_URL}/subscriptions/create-checkout`,
      {
        priceId,
        successUrl,
        cancelUrl,
      },
      {
        headers: {
          Authorization: jwtToken ? `Bearer ${jwtToken}` : "",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error creating checkout session:", error);
    throw error;
  }
};

/**
 * Check if a subscription checkout was successful
 */
export const verifyCheckoutSession = async (
  sessionId: string,
  clerkToken?: string
): Promise<{ success: boolean; subscription?: any }> => {
  try {
    // First exchange the Clerk token for a JWT token
    let jwtToken;

    if (clerkToken) {
      jwtToken = await getJwtToken(clerkToken);
    } else {
      console.warn("No authentication token available");
      throw new Error("Authentication required");
    }

    const response = await axios.get(
      `${API_URL}/subscriptions/verify-session/${sessionId}`,
      {
        headers: {
          Authorization: jwtToken ? `Bearer ${jwtToken}` : "",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error verifying checkout session:", error);
    throw error;
  }
};
